import { loadEncounters } from "../../data/loadEncounters";
import { getSystem, loadSystems } from "../../data/loadSystems";
import { shortestRoute } from "../../haulingMissions";
import { isVipDeadlineExpired, type AcceptedMission } from "../../acceptedMissions";
import { effectiveNow } from "../../gameClock";
import { applyAbandonStanding } from "../../factionStanding";
import { triggerSave } from "../persistence";
import { state } from "../state";
import type { ScreenContext } from "../app";
import { formatSeconds, renderHeader } from "../components";

const encounters = loadEncounters();
const systems = loadSystems();

function hopsFromCurrent(targetSystemId: string): number {
  if (targetSystemId === state.currentSystemId) {
    return 0;
  }
  return shortestRoute(state.currentSystemId, targetSystemId, systems).hops;
}

export function renderMissionDiaryScreen(container: HTMLElement, ctx: ScreenContext): void {
  if (!state.currentShip) {
    ctx.navigate("ships");
    return;
  }

  renderHeader(container, ctx);

  const wrap = document.createElement("div");
  wrap.innerHTML = `
    <h1>Mission Diary</h1>
    <p class="dim">Every currently-accepted mission (16.34, 16.35). Completion is event-driven -- Hauling/VIP
    complete automatically on arrival, checked opportunistically every render. Combat/Mining each create a
    real Location in their target System (Scanner UI, once you're in-system) -- Fast Travel there and
    fight/mine it out for real; there's no separate button here for either.</p>
    <div class="mission-list">
      ${
        state.acceptedMissions.length === 0
          ? '<p class="dim">No accepted missions. Visit the Mission Board to pick some up.</p>'
          : state.acceptedMissions.map((m) => diaryRowHtml(m)).join("")
      }
    </div>
  `;

  wrap.querySelectorAll<HTMLButtonElement>("button[data-waypoint]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.waypointSystemId = btn.dataset.waypoint!;
      ctx.rerender();
    });
  });

  wrap.querySelectorAll<HTMLButtonElement>("button[data-abandon]").forEach((btn) => {
    btn.addEventListener("click", () => abandonMission(btn.dataset.abandon!, ctx));
  });

  wrap.querySelectorAll<HTMLButtonElement>("button[data-dev-complete]").forEach((btn) => {
    btn.addEventListener("click", () => devCompleteMission(btn.dataset.devComplete!, ctx));
  });

  container.appendChild(wrap);
}

// DEV DEBUG-only shortcut (Don's direction: "even that button should show
// up when we have toggled DEV DEBUG"): pays out and removes a Combat/Mining
// mission unconditionally, skipping the real Fast-Travel-there-and-fight/
// mine-it-out flow (16.35) entirely -- for testing mission flow/rewards
// without replaying combat or mining every time.
function devCompleteMission(missionId: string, ctx: ScreenContext): void {
  const mission = state.acceptedMissions.find(
    (m): m is Extract<AcceptedMission, { type: "Combat" | "Mining" }> =>
      m.id === missionId && (m.type === "Combat" || m.type === "Mining")
  );
  if (!mission) return;
  state.credits += mission.rewardCr;
  state.acceptedMissions = state.acceptedMissions.filter((m) => m.id !== missionId);
  state.lastEvent = `DEV: completed ${mission.type} mission instantly -- earned ${mission.rewardCr} CR.`;
  triggerSave(ctx);
  ctx.rerender();
}

function diaryRowHtml(mission: AcceptedMission): string {
  const abandonBtn =
    mission.type === "VIP Transport"
      ? ""
      : `<button type="button" data-abandon="${mission.id}">Abandon</button>`;

  if (mission.type === "Combat") {
    const encounter = encounters.find((e) => e.id === mission.encounterId);
    const system = getSystem(mission.systemId);
    const here = state.currentSystemId === mission.systemId;
    const hops = hopsFromCurrent(mission.systemId);
    return `
      <div class="mission-card">
        <div class="mission-card-title">
          <span class="pill">Combat</span>
          <strong>${encounter?.name ?? mission.encounterId}</strong>
          <span class="dim">@ ${system?.name ?? mission.systemId} -- ${hops} hops from here</span>
        </div>
        <div class="dim">Reward ${mission.rewardCr} CR</div>
        <button type="button" data-waypoint="${mission.systemId}">Set Waypoint</button>
        <div class="dim">
          ${
            here
              ? `Fast Travel to "Mission: ${encounter?.name ?? mission.encounterId}" in the Scanner UI to engage.`
              : `Travel to ${system?.name ?? mission.systemId} first.`
          }
        </div>
        ${state.devDebugOn ? `<button type="button" data-dev-complete="${mission.id}">DEV: Complete Instantly</button>` : ""}
        ${abandonBtn}
      </div>
    `;
  }

  if (mission.type === "Mining") {
    const system = getSystem(mission.systemId);
    const here = state.currentSystemId === mission.systemId;
    const hops = hopsFromCurrent(mission.systemId);
    return `
      <div class="mission-card">
        <div class="mission-card-title">
          <span class="pill">Mining</span>
          <strong>${mission.taggedM3.toFixed(1)} / ${mission.quotaM3} m3 ${mission.resource}</strong>
          <span class="dim">@ ${system?.name ?? mission.systemId} -- ${hops} hops from here</span>
        </div>
        <div class="dim">Reward ${mission.rewardCr} CR</div>
        <button type="button" data-waypoint="${mission.systemId}">Set Waypoint</button>
        <div class="dim">
          ${
            here
              ? `Fast Travel to "Mission: ${mission.resource} Deposit" in the Scanner UI to mine.`
              : `Travel to ${system?.name ?? mission.systemId} first.`
          }
        </div>
        ${state.devDebugOn ? `<button type="button" data-dev-complete="${mission.id}">DEV: Complete Instantly</button>` : ""}
        ${abandonBtn}
      </div>
    `;
  }

  if (mission.type === "Hauling") {
    const destination = getSystem(mission.destinationSystemId);
    const hops = hopsFromCurrent(mission.destinationSystemId);
    return `
      <div class="mission-card">
        <div class="mission-card-title">
          <span class="pill">Hauling</span>
          <strong>Deliver to ${destination?.name ?? mission.destinationSystemId}</strong>
          <span class="dim">${hops} hops from here</span>
        </div>
        <div class="dim">Reward ${mission.rewardCr} CR -- completes automatically on arrival</div>
        <button type="button" data-waypoint="${mission.destinationSystemId}">Set Waypoint</button>
        ${abandonBtn}
      </div>
    `;
  }

  // VIP Transport
  const destination = getSystem(mission.destinationSystemId);
  const hops = hopsFromCurrent(mission.destinationSystemId);
  const now = effectiveNow(state.clock, Date.now());
  const remainingMs = mission.acceptedAt + mission.deadlineSeconds * 1000 - now;
  const countdown = isVipDeadlineExpired(mission, now) ? "expired" : formatSeconds(remainingMs / 1000);
  return `
    <div class="mission-card">
      <div class="mission-card-title">
        <span class="pill">VIP Transport</span>
        <strong>Deliver to ${destination?.name ?? mission.destinationSystemId}</strong>
        <span class="dim">${hops} hops from here</span>
      </div>
      <div class="dim">Reward ${mission.rewardCr} CR -- completes automatically on arrival</div>
      <div class="${remainingMs < 0 ? "flee" : "dim"}">Deadline in ${countdown}</div>
      <button type="button" data-waypoint="${mission.destinationSystemId}">Set Waypoint</button>
    </div>
  `;
}

function abandonMission(missionId: string, ctx: ScreenContext): void {
  const mission = state.acceptedMissions.find((m) => m.id === missionId);
  if (!mission) {
    return;
  }
  // Per-type abandon behavior (16.34) -- deliberately different, not a
  // single generic rule. Mining/Hauling both just eject their cargo
  // (mission-tagged units, or the whole sealed container) with no
  // payment -- since neither is modeled as separate general-cargo state
  // in this build, removing the accepted mission itself IS that ejection.
  if (mission.type === "Combat" && state.waypointSystemId === mission.systemId) {
    state.waypointSystemId = null;
  }
  // Abandon (player-initiated, not natural failure) costs -1.0 standing
  // with that mission's poster (16.55) -- natural failure (VIP deadline
  // expiry, handled entirely by missionChecks.ts's runOpportunisticChecks)
  // still carries zero penalty, per Section 14's unchanged low-stakes-
  // failure rule.
  state.stats.missionsAbandoned++;
  state.standing = applyAbandonStanding(state.standing, mission.posterFaction);
  state.acceptedMissions = state.acceptedMissions.filter((m) => m.id !== missionId);
  state.lastEvent = `Abandoned: ${mission.type} mission.`;
  triggerSave(ctx);
  ctx.rerender();
}
